/**
 * Measures the box a chart is drawn into, so the SVG can be laid out in real CSS pixels.
 *
 * The templates used to draw into a fixed `viewBox` stretched to fit with
 * `preserveAspectRatio="none"`, which scaled the TEXT along with the geometry: the same view
 * rendered squashed on a narrow dashboard card and stretched wide on a big one, with tick
 * labels and titles distorting either way. Drawing at the measured size instead means one
 * chart-pixel is one screen-pixel — type stays 11px and circular points stay circular at every
 * card size, and only the plot area changes when a card is resized.
 *
 * Returns `0 × 0` for the first paint (before the observer has fired); callers render nothing
 * until they have a real box, which lasts a single frame.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface ChartSize {
  /** Attach to the element the chart should fill. */
  ref: (element: HTMLElement | null) => void;
  width: number;
  height: number;
}

export function useChartSize(): ChartSize {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((element: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!element) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (!box) {
        return;
      }
      const width = Math.round(box.width);
      const height = Math.round(box.height);
      // Bail on an unchanged box: a resize observer that always sets state re-renders the
      // whole chart on every scroll-driven reflow, and a rounded no-op change would loop.
      setSize((prev) =>
        prev.width === width && prev.height === height ? prev : { width, height },
      );
    });
    observer.observe(element);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { ref, ...size };
}
